<template>
  <SimpleNotesPageShell
    :title="text.page.title"
    :subtitle="text.page.subtitle"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #metrics>
      <section class="metrics-grid invoices-kpis-grid" :style="kpiGridStyle">
        <SimpleNotesKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <SimpleNotesToolbar
        :filter-options="filterOptions"
        :labels="text.toolbar"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />
    </template>

    <template #tabs>
      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <SimpleNotesTable
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="pagination"
        :labels="text.table"
        :loading-text="text.table.loading"
        :empty-text="text.table.empty"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @view-note="viewSimpleNote"
        @pay-note="paySimpleNote"
        @download-note="downloadSimpleNote"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />
    </template>
  </SimpleNotesPageShell>

  <PayModal
    :show="showPayNoteModal"
    :row="payNoteRow"
    :submitting="payNoteSubmitting"
    @close="closePayNoteModal"
    @submit="submitPayNoteModal"
  />

  <ExportFormatModal
    :show="isSimpleNotesExportModalOpen"
    :text-catalog="text"
    :export-service="simpleNotesExportService"
    file-name-prefix="notas_simples"
    @close="closeSimpleNotesExportModal"
  />

  <DocumentsDownloadModal
    :show="showDownloadModal"
    :labels="text.downloadModal"
    :context="downloadScopeContext"
    :latest-request="latestDownloadRequest"
    :package-limit="downloadPackageLimit"
    :expires-hours="downloadExpiresHours"
    :submitting="downloadRequestSubmitting"
    :document-type-options="['pdf']"
    :allow-group-by="true"
    @close="closeDownloadModal"
    @refresh="refreshLatestDownloadRequest"
    @create="createDownloadRequest"
    @download-package="downloadRequestPackage"
    @retry-package="retryDownloadRequestPackage"
    @regenerate="regenerateDownloadRequest"
  />
</template>

<script>
import SimpleNotesPageShell from '@/components/simple-notes/SimpleNotesPageShell.vue';
import SimpleNotesToolbar from '@/components/simple-notes/SimpleNotesToolbar.vue';
import SimpleNotesTable from '@/components/simple-notes/SimpleNotesTable.vue';
import SimpleNotesKpiCard from '@/components/simple-notes/SimpleNotesKpiCard.vue';
import DocumentsDownloadModal from '@/components/common/downloads/DocumentsDownloadModal.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  buildInvoiceStatusTabs,
  filterInvoicesByStatus,
} from '@/composables/invoices/useInvoicesStatusTabs';
import customersService from '@/services/customers';
import areasService from '@/services/areas';
import salesFlatRatesService from '@/services/salesFlatRates';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import simpleNotesService from '@/services/simpleNotes';
import paymentReceiptsService from '@/services/paymentReceipts';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { downloadBlobFile } from '@/utils/fileDownload';
import { formatDateShort } from '@/utils/datePresentation';

const { catalog: text } = useDomainText('simpleNotes');
const DOWNLOAD_REQUEST_POLLING_MS = 15000;

const defaultToolbarFilters = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const toDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  return {
    date_from: toDate(start),
    date_to: toDate(end),
    customer_ids: [],
    business_ids: [],
    area_ids: [],
  };
};

const defaultFilterOptions = () => ({
  customer: [],
  business: [],
  area: [],
});

const normalizeSimpleNotesToolbarFilters = (filters) => {
  const safeFilters = filters && typeof filters === 'object' ? filters : {};

  return {
    date_from: String(safeFilters.date_from || '').trim(),
    date_to: String(safeFilters.date_to || '').trim(),
    customer_ids: Array.isArray(safeFilters.customer_ids) ? safeFilters.customer_ids : [],
    business_ids: Array.isArray(safeFilters.business_ids) ? safeFilters.business_ids : [],
    area_ids: Array.isArray(safeFilters.area_ids) ? safeFilters.area_ids : [],
  };
};

const buildSimpleNotesListParams = ({ toolbarSearch = '', toolbarFilters = {} } = {}) => {
  return {
    search: String(toolbarSearch || '').trim(),
    date_start: String(toolbarFilters?.date_from || '').trim(),
    date_end: String(toolbarFilters?.date_to || '').trim(),
    customer_id: Array.isArray(toolbarFilters?.customer_ids) ? toolbarFilters.customer_ids : [],
    sale_id: Array.isArray(toolbarFilters?.business_ids) ? toolbarFilters.business_ids : [],
    area_id: Array.isArray(toolbarFilters?.area_ids) ? toolbarFilters.area_ids : [],
  };
};

const mergeCatalogOptionsByValue = (...groups) => {
  const merged = [];
  const seen = new Set();

  groups.forEach((items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      const value = String(item?.value || '').trim();
      if (value === '' || seen.has(value)) {
        return;
      }

      seen.add(value);
      merged.push(item);
    });
  });

  return merged;
};

const normalizeCustomerIds = (customerIds = []) => {
  return [...new Set(
    (Array.isArray(customerIds) ? customerIds : [])
      .map((value) => Number(value || 0) || 0)
      .filter((value) => value > 0)
  )];
};

export default {
  name: 'SimpleNotesPage',
  components: {
    SimpleNotesPageShell,
    SimpleNotesToolbar,
    SimpleNotesTable,
    SimpleNotesKpiCard,
    StatusTabs,
    DocumentsDownloadModal,
    PayModal,
    ExportFormatModal,
  },
  data() {
    return {
      text,
      simpleNotesExportService: simpleNotesService,
      tableLoading: false,
      rows: [],
      filterOptions: defaultFilterOptions(),
      toolbarSearch: '',
      toolbarFilters: defaultToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: defaultToolbarFilters(),
      metricsValues: {
        totalInvoiced: 0,
        totalCollected: 0,
        pendingBalance: 0,
        averagePerNote: 0,
      },
      activeStatusTab: 'all',
      currentPage: 1,
      pageSize: 20,
      sortField: 'date',
      sortDirection: 'desc',
      listRequestToken: 0,
      showPayNoteModal: false,
      payNoteSubmitting: false,
      payNoteRow: null,
      isSimpleNotesExportModalOpen: false,
      showDownloadModal: false,
      downloadRequestSubmitting: false,
      latestDownloadRequest: null,
      downloadPackageLimit: 200,
      downloadExpiresHours: 24,
      downloadRequestPollingId: null,
    };
  },
  computed: {
    headerActions() {
      return [
        {
          key: 'simple-note',
          label: this.text.headerActions.create,
          icon: 'fa-sticky-note-o',
          variant: 'success',
        },
        {
          key: 'excel',
          label: this.text.headerActions.exportExcel,
          icon: 'fa-file-excel-o',
          variant: 'secondary',
          className: 'app-header-btn--export',
        },
        {
          key: 'download',
          label: this.text.headerActions.exportDownload,
          icon: 'fa-download',
          variant: 'secondary',
          className: 'app-header-btn--download-list',
        },
      ];
    },
    toolbarFilteredRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    statusTabs() {
      return buildInvoiceStatusTabs(this.toolbarFilteredRows, this.text.tabs || {});
    },
    statusFilteredRows() {
      return filterInvoicesByStatus(this.toolbarFilteredRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.statusFilteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        date: (row) => new Date(row?.date || 0).getTime(),
        customer: (row) => String(row?.customer_name || row?.company_name || '').trim().toLowerCase(),
        folio: (row) => String(row?.folio || '').trim().toLowerCase(),
        total: (row) => Number(row?.total || 0),
        balance: (row) => Number(row?.balance || 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    pagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    kpiCards() {
      const labels = this.text.metrics || {};

      return [
        {
          title: labels.totalInvoiced,
          value: formatCurrency(Number(this.metricsValues.totalInvoiced || 0)),
          icon: 'fa-file-text-o',
          iconBg: '#e8f0fe',
          iconColor: '#2563eb',
        },
        {
          title: labels.totalCollected,
          value: formatCurrency(Number(this.metricsValues.totalCollected || 0)),
          icon: 'fa-check-circle-o',
          iconBg: '#e8f7ef',
          iconColor: '#1f8a4c',
        },
        {
          title: labels.pendingBalance,
          value: formatCurrency(Number(this.metricsValues.pendingBalance || 0)),
          icon: 'fa-clock-o',
          iconBg: '#fff4e8',
          iconColor: '#b45309',
        },
        {
          title: labels.averagePerNote,
          value: formatCurrency(Number(this.metricsValues.averagePerNote || 0)),
          icon: 'fa-list-ol',
          iconBg: '#f3efff',
          iconColor: '#6d28d9',
        },
      ];
    },
    kpiGridStyle() {
      const totalCards = Array.isArray(this.kpiCards) ? this.kpiCards.length : 0;
      const columns = Math.min(Math.max(totalCards, 1), 5);

      return {
        '--invoices-kpi-columns': String(columns),
      };
    },
    activeStatusTabLabel() {
      const activeTab = (Array.isArray(this.statusTabs) ? this.statusTabs : [])
        .find((tab) => String(tab?.key || '') === String(this.activeStatusTab || 'all'));

      return String(activeTab?.label || this.text?.tabs?.all || '').trim();
    },
    downloadScopeLabel() {
      const parts = ['Notas simples'];
      if (this.activeStatusTabLabel !== '') {
        parts.push(this.activeStatusTabLabel);
      }

      const dateFrom = String(this.appliedToolbarFilters?.date_from || '').trim();
      const dateTo = String(this.appliedToolbarFilters?.date_to || '').trim();
      if (dateFrom !== '' || dateTo !== '') {
        const dateFromLabel = dateFrom !== '' ? formatDateShort(dateFrom, { fallback: dateFrom }) : '...';
        const dateToLabel = dateTo !== '' ? formatDateShort(dateTo, { fallback: dateTo }) : '...';
        parts.push(`${dateFromLabel} - ${dateToLabel}`);
      }

      return parts.join(' · ');
    },
    downloadScopeContext() {
      return {
        totalInvoices: this.statusFilteredRows.length,
        scopeDescription: String(this.text?.downloadModal?.contextMessage || '').trim(),
        scopeLabel: this.downloadScopeLabel,
      };
    },
  },
  mounted() {
    this.loadRows();
    this.loadFilterOptions();
  },
  beforeUnmount() {
    this.stopDownloadRequestPolling();
  },
  methods: {
    buildListParams() {
      return buildSimpleNotesListParams({
        toolbarSearch: this.appliedToolbarSearch,
        toolbarFilters: this.appliedToolbarFilters,
      });
    },
    mapMetrics(payloadMetrics = {}, rows = []) {
      const fallbackTotals = (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
        const total = Number(row?.total || 0) || 0;
        const balance = Number(row?.balance || 0) || 0;
        acc.total += total;
        acc.balance += balance;
        return acc;
      }, { total: 0, balance: 0 });

      const notesCount = Number(payloadMetrics?.invoice_count ?? rows.length ?? 0) || 0;
      const totalInvoiced = Number(payloadMetrics?.total_invoiced ?? fallbackTotals.total) || 0;
      const pendingBalance = Number(payloadMetrics?.pending_balance ?? fallbackTotals.balance) || 0;
      const totalCollected = Number(payloadMetrics?.total_collected ?? (totalInvoiced - pendingBalance)) || 0;
      const averagePerNote = Number(payloadMetrics?.average_per_note ?? payloadMetrics?.invoice_average ?? (notesCount > 0 ? totalInvoiced / notesCount : 0)) || 0;

      return {
        averagePerNote,
        totalInvoiced,
        pendingBalance,
        totalCollected,
      };
    },
    async loadRows() {
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const response = await simpleNotesService.index(this.buildListParams());
        const payload = extractPayload(response);
        const rows = extractCollection(response);

        if (requestToken === this.listRequestToken) {
          this.rows = Array.isArray(rows) ? rows : [];
          this.metricsValues = this.mapMetrics(payload?.metrics || {}, this.rows);
          this.currentPage = 1;
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.rows = [];
          this.metricsValues = this.mapMetrics({}, []);
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
        }
      }
    },
    async fetchBusinessFilterOptions(customerIds = []) {
      const normalizedCustomerIds = normalizeCustomerIds(customerIds);
      const params = {};

      if (normalizedCustomerIds.length === 1) {
        params.customer_id = normalizedCustomerIds[0];
      }

      if (normalizedCustomerIds.length > 1) {
        params.customer = normalizedCustomerIds;
      }

      try {
        const [flatRatesResponse, multiCustomersResponse] = await Promise.all([
          salesFlatRatesService.businesses(params, { skipLoading: true }),
          salesMultiCustomerService.index(params, { skipLoading: true }),
        ]);

        const flatRateOptions = normalizeCatalogOptions(extractCollection(flatRatesResponse), { useCodeInLabel: false });
        const multiCustomerOptions = normalizeCatalogOptions(extractCollection(multiCustomersResponse), { useCodeInLabel: false });

        return mergeCatalogOptionsByValue(flatRateOptions, multiCustomerOptions);
      } catch (error) {
        return [];
      }
    },
    async loadFilterOptions() {
      try {
        const [customersResponse, areasResponse, businessOptions] = await Promise.all([
          customersService.selectors(),
          areasService.index(),
          this.fetchBusinessFilterOptions(),
        ]);

        const customerOptions = normalizeCatalogOptions(extractCollection(customersResponse), { useCodeInLabel: false });
        const areaOptions = normalizeCatalogOptions(extractCollection(areasResponse), { useCodeInLabel: false });

        this.filterOptions = {
          ...defaultFilterOptions(),
          customer: customerOptions,
          business: businessOptions,
          area: areaOptions,
        };
      } catch (error) {
        this.filterOptions = defaultFilterOptions();
      }
    },
    normalizeIdSelection(values = []) {
      return [...new Set(
        (Array.isArray(values) ? values : [])
          .map((value) => Number(value || 0) || 0)
          .filter((value) => value > 0)
      )].sort((left, right) => left - right);
    },
    hasSameIdSelection(left = [], right = []) {
      const normalizedLeft = this.normalizeIdSelection(left);
      const normalizedRight = this.normalizeIdSelection(right);

      if (normalizedLeft.length !== normalizedRight.length) {
        return false;
      }

      return normalizedLeft.every((value, index) => value === normalizedRight[index]);
    },
    async loadBusinessFilterOptions(customerIds = []) {
      const businessOptions = await this.fetchBusinessFilterOptions(customerIds);
      const allowedBusinessIds = new Set(
        (Array.isArray(businessOptions) ? businessOptions : [])
          .map((option) => String(option?.value || '').trim())
          .filter((value) => value !== '')
      );

      const normalizeBusinessIds = (values = []) => {
        return (Array.isArray(values) ? values : [])
          .map((value) => String(value || '').trim())
          .filter((value) => value !== '' && allowedBusinessIds.has(value));
      };

      const nextToolbarBusinessIds = normalizeBusinessIds(this.toolbarFilters?.business_ids);
      const nextAppliedBusinessIds = normalizeBusinessIds(this.appliedToolbarFilters?.business_ids);

      this.filterOptions = {
        ...this.filterOptions,
        business: businessOptions,
      };

      this.toolbarFilters = {
        ...this.toolbarFilters,
        business_ids: nextToolbarBusinessIds,
      };

      this.appliedToolbarFilters = {
        ...this.appliedToolbarFilters,
        business_ids: nextAppliedBusinessIds,
      };
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'simple-note') {
        this.$router.push({ name: 'simpleNoteCreate' });
        return;
      }

      if (actionKey === 'excel') {
        this.openSimpleNotesExportModal();
        return;
      }

      if (actionKey === 'download') {
        this.openDownloadModal();
      }
    },
    normalizeLatestDownloadRequest(payload) {
      const safePayload = payload && typeof payload === 'object' ? payload : {};
      const request = safePayload?.request && typeof safePayload.request === 'object'
        ? safePayload.request
        : null;

      this.downloadPackageLimit = Number(safePayload?.package_limit || this.downloadPackageLimit || 200) || 200;
      this.downloadExpiresHours = Number(safePayload?.expires_hours || this.downloadExpiresHours || 24) || 24;
      this.latestDownloadRequest = request;
    },
    async refreshLatestDownloadRequest() {
      try {
        const response = await simpleNotesService.downloadRequestsLatest({}, { skipLoading: true });
        const payload = extractPayload(response);
        this.normalizeLatestDownloadRequest(payload);
      } catch (error) {
        // Keep modal usable even when latest request cannot be loaded.
      }

      this.syncDownloadRequestPolling();
    },
    openDownloadModal() {
      this.showDownloadModal = true;
      this.refreshLatestDownloadRequest();
    },
    closeDownloadModal() {
      this.showDownloadModal = false;
      this.stopDownloadRequestPolling();
    },
    hasActiveDownloadProcessing() {
      const status = String(this.latestDownloadRequest?.status || '').trim().toLowerCase();
      return status === 'pending' || status === 'processing';
    },
    stopDownloadRequestPolling() {
      if (this.downloadRequestPollingId) {
        window.clearInterval(this.downloadRequestPollingId);
        this.downloadRequestPollingId = null;
      }
    },
    syncDownloadRequestPolling() {
      const shouldPoll = this.showDownloadModal && this.hasActiveDownloadProcessing();
      if (!shouldPoll) {
        this.stopDownloadRequestPolling();
        return;
      }

      if (this.downloadRequestPollingId) {
        return;
      }

      this.downloadRequestPollingId = window.setInterval(() => {
        if (!this.showDownloadModal) {
          this.stopDownloadRequestPolling();
          return;
        }

        this.refreshLatestDownloadRequest();
      }, DOWNLOAD_REQUEST_POLLING_MS);
    },
    buildDownloadRequestPayload(groupBy = 'none') {
      const resolvedGroupBy = String(groupBy || 'none').trim().toLowerCase();

      return {
        document_type: 'pdf',
        group_by: ['none', 'customer', 'month'].includes(resolvedGroupBy) ? resolvedGroupBy : 'none',
        tab_key: String(this.activeStatusTab || 'all').trim().toLowerCase() || 'all',
        tab_label: this.activeStatusTabLabel,
        scope_label: this.downloadScopeLabel,
        search: String(this.appliedToolbarSearch || '').trim(),
        list_params: this.buildListParams(),
      };
    },
    resolveDownloadFileNameFromHeaders(headers = {}, fallback = 'notas_simples_paquete_1_de_1.zip') {
      const contentDisposition = String(headers?.['content-disposition'] || headers?.['Content-Disposition'] || '').trim();
      if (contentDisposition === '') {
        return fallback;
      }

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(String(utf8Match[1]).trim());
        } catch (error) {
          return String(utf8Match[1]).trim() || fallback;
        }
      }

      const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      return String(basicMatch?.[1] || '').trim() || fallback;
    },
    async createDownloadRequest(payload = {}) {
      if (this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;
      const requestPayload = this.buildDownloadRequestPayload(payload?.groupBy || 'none');
      const totalNotes = Number(this.statusFilteredRows.length || 0) || 0;
      const isSinglePackageScope = totalNotes > 0 && totalNotes <= Number(this.downloadPackageLimit || 200);

      try {
        if (isSinglePackageScope) {
          const response = await simpleNotesService.downloadRequestsDirectDownload(requestPayload);
          const fileName = this.resolveDownloadFileNameFromHeaders(response?.headers || {}, 'notas_simples_paquete_1_de_1.zip');
          downloadBlobFile(response?.data ?? response, fileName, 'application/zip');
          await this.refreshLatestDownloadRequest();
          this.closeDownloadModal();
          return;
        }

        const response = await simpleNotesService.downloadRequestsStore(requestPayload);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.downloadRequestCreated || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadRequestError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    async regenerateDownloadRequest(payload = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      if (requestId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await simpleNotesService.downloadRequestsRegenerate(requestId, {
          document_type: 'pdf',
          group_by: ['none', 'customer', 'month'].includes(String(payload?.groupBy || '').trim().toLowerCase())
            ? String(payload?.groupBy || '').trim().toLowerCase()
            : 'none',
        });
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.regenerateSuccess || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.regenerateError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    resolveDownloadPackageFileName(packageRow = {}) {
      const rawName = String(packageRow?.file_name || '').trim();
      if (rawName !== '') {
        return rawName;
      }

      const index = Number(packageRow?.package_index || 1) || 1;
      const total = Number(this.latestDownloadRequest?.total_packages || 1) || 1;
      return `notas_simples_paquete_${index}_de_${total}.zip`;
    },
    async downloadRequestPackage(packageRow = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      const packageId = Number(packageRow?.id || 0) || 0;
      if (requestId <= 0 || packageId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await simpleNotesService.downloadRequestsDownloadPackage(requestId, packageId);
        const fileName = this.resolveDownloadPackageFileName(packageRow);
        downloadBlobFile(response?.data ?? response, fileName, 'application/zip');
        await this.refreshLatestDownloadRequest();
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadPackageError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
        await this.refreshLatestDownloadRequest();
      } finally {
        this.downloadRequestSubmitting = false;
      }
    },
    async retryDownloadRequestPackage(packageRow = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      const packageId = Number(packageRow?.id || 0) || 0;
      if (requestId <= 0 || packageId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await simpleNotesService.downloadRequestsRetryPackage(requestId, packageId);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.retryPackageSuccess || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.retryPackageError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    openSimpleNotesExportModal() {
      this.isSimpleNotesExportModalOpen = true;
    },
    closeSimpleNotesExportModal() {
      this.isSimpleNotesExportModalOpen = false;
    },
    handleToolbarSearch(searchValue) {
      const nextSearch = String(searchValue || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
      this.loadRows();
    },
    async handleToolbarFilters(filters) {
      const previousDateStart = String(this.toolbarFilters?.date_from || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_to || '').trim();
      const previousCustomerIds = Array.isArray(this.toolbarFilters?.customer_ids)
        ? this.toolbarFilters.customer_ids
        : [];
      const normalizedFilters = normalizeSimpleNotesToolbarFilters(filters);

      this.toolbarFilters = normalizedFilters;

      const customerChanged = !this.hasSameIdSelection(previousCustomerIds, normalizedFilters.customer_ids);
      if (customerChanged) {
        await this.loadBusinessFilterOptions(normalizedFilters.customer_ids);
      }

      const nextDateStart = String(normalizedFilters?.date_from || '').trim();
      const nextDateEnd = String(normalizedFilters?.date_to || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;

      if (dateChanged) {
        this.appliedToolbarSearch = String(this.toolbarSearch || '').trim();
        this.appliedToolbarFilters = normalizedFilters;
        this.loadRows();
      }
    },
    async applyToolbarFilters(payload) {
      const nextSearch = String(payload?.search ?? this.toolbarSearch ?? '').trim();
      const nextFilters = normalizeSimpleNotesToolbarFilters(payload?.filters ?? this.toolbarFilters);
      const customerChanged = !this.hasSameIdSelection(this.toolbarFilters?.customer_ids, nextFilters.customer_ids);

      this.toolbarSearch = nextSearch;
      this.toolbarFilters = nextFilters;

      if (customerChanged) {
        await this.loadBusinessFilterOptions(nextFilters.customer_ids);
      }

      this.appliedToolbarSearch = nextSearch;
      this.appliedToolbarFilters = nextFilters;
      this.loadRows();
    },
    handlePageChange(nextPage) {
      this.currentPage = ensureValidPage(nextPage, this.totalPages);
    },
    handleSortChange(payload) {
      const sort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        {
          field: payload?.field,
          direction: payload?.direction,
        },
      );

      this.sortField = sort.field;
      this.sortDirection = sort.direction;
      this.currentPage = 1;
    },
    selectStatusTab(tabKey) {
      const nextKey = String(tabKey || '').trim();
      const isValid = this.statusTabs.some((tab) => String(tab?.key || '') === nextKey);
      if (!isValid) {
        return;
      }

      this.activeStatusTab = nextKey;
      this.currentPage = 1;
    },
    viewSimpleNote(row) {
      const simpleNoteId = Number(row?.id || 0) || 0;
      if (simpleNoteId <= 0) {
        return;
      }

      this.$router.push({
        name: 'simpleNoteDetail',
        params: {
          simpleNoteId: String(simpleNoteId),
        },
      });
    },
    buildPayNoteModalRow(row = {}) {
      const saleId = Number(row?.sale_id || 0) || 0;
      if (saleId <= 0 && row?.has_sale !== true) {
        return null;
      }

      const paymentId = Number(row?.payment_id || row?.payment?.id || 0) || 0;
      const customerId = Number(row?.customer_id || row?.company_id || 0) || 0;
      const total = Number(row?.balance || row?.total || 0) || 0;

      if (paymentId <= 0 || customerId <= 0 || total <= 0) {
        return null;
      }

      return {
        id: paymentId,
        customer_id: customerId,
        company_id: customerId,
        customer_name: String(row?.customer_name || row?.company_name || '').trim(),
        company_name: String(row?.company_name || row?.customer_name || '').trim(),
        date: String(row?.date || '').trim(),
        currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
        exchange_rate: Number(row?.exchange_rate || 1) || 1,
        total,
        subtotal: total,
        simple_note_id: Number(row?.id || 0) || 0,
        invoice: `${String(row?.serial || '').trim()}${String(row?.folio || '').trim()}`.trim(),
        voucher_type: 'simple_note',
        special: 1,
      };
    },
    paySimpleNote(row) {
      const modalRow = this.buildPayNoteModalRow(row);
      if (!modalRow) {
        this.$toast?.error('No se puede pagar una nota simple sin negocio relacionado.');
        return;
      }

      this.payNoteRow = modalRow;
      this.showPayNoteModal = true;
    },
    closePayNoteModal(force = false) {
      if (this.payNoteSubmitting && !force) {
        return;
      }

      this.showPayNoteModal = false;
      this.payNoteRow = null;
    },
    async submitPayNoteModal(payload) {
      this.payNoteSubmitting = true;

      try {
        const simpleNoteId = Number(this.payNoteRow?.simple_note_id || 0) || 0;
        if (simpleNoteId <= 0) {
          this.$toast?.error('No se pudo identificar la nota simple para registrar el cobro.');
          return;
        }

        const response = await paymentReceiptsService.store({
          ...payload,
          voucher_type: 'simple_note',
          special: 1,
          simple_note_id: simpleNoteId,
          payment_plan_id: 0,
          should_stamp_payment: 0,
          stamp_payment: 0,
          timbrar: 0,
        });
        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible registrar el pago de la nota simple.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'Pago de nota simple registrado correctamente.'));
        this.closePayNoteModal(true);
        await this.loadRows();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible registrar el pago de la nota simple.');
      } finally {
        this.payNoteSubmitting = false;
      }
    },
    async downloadSimpleNote(row) {
      const simpleNoteId = Number(row?.id || 0) || 0;
      if (simpleNoteId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.missingRowForDownload || ''));
        return;
      }

      try {
        const response = await simpleNotesService.downloadPdf(simpleNoteId);
        this.openPdfBlob(response?.data ?? response, this.buildSimpleNoteDocumentBaseName(row));
      } catch (error) {
        const fallbackMessage = 'No fue posible descargar el PDF de la nota simple.';
        this.$toast?.error(extractApiErrorMessage(error, fallbackMessage));
      }
    },
    buildSimpleNoteDocumentBaseName(row) {
      const serial = String(row?.serial || 'NS').trim().toUpperCase();
      const folio = String(row?.folio || '').trim().toUpperCase();
      const simpleNoteId = Number(row?.id || 0) || 0;
      const fallback = simpleNoteId > 0 ? `nota_simple_${simpleNoteId}` : 'nota_simple';
      const reference = `${serial}${folio}`.trim();

      return (reference !== '' ? reference : fallback)
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_\-]/g, '') || fallback;
    },
    openPdfBlob(payload, fileBaseName = 'nota_simple') {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' });

      const objectUrl = window.URL.createObjectURL(blob);
      const opened = window.open(objectUrl, '_blank', 'noopener');

      if (!opened) {
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = `${fileBaseName}.pdf`;
        link.click();
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    },
  },
};
</script>

<style scoped>
.invoices-kpis-grid {
  grid-template-columns: repeat(var(--invoices-kpi-columns, 4), minmax(0, 1fr));
}

@media (max-width: 1500px) {
  .invoices-kpis-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .invoices-kpis-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .invoices-kpis-grid {
    grid-template-columns: 1fr;
  }
}
</style>

